import type { Optional } from 'utility-types'
import React, { type FC, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react'
import { isPending, usePromise } from 'react-sync-promise'

import { isArrayNotEmpty, isDateAfter, nameFunction, type ReadonlyDate, toggleReadonlySet } from '../../../utils'

import type { ConnectwareError, CybusLog, CybusLogLevel, CybusLogSource } from '../../../domain'

import type { LogOptions } from '../../../application'

import { useAppUsecase } from '../State'
import { createExtendedState } from '../utils'

type LogsState =
    & Readonly<{
        // Filters
        source: CybusLogSource
        levels: readonly CybusLogLevel[]
        startDate: ReadonlyDate | null
        endDate: ReadonlyDate | null

        // The logs that are currently loaded
        logsPromise: Promise<readonly CybusLog[]> | null

        // User selected logs
        selectedLog: CybusLog | null
    }>
    & Pick<LogOptions, 'resource' | 'lines'>

const { Provider: LogsStateProvider, useExtendedState: useLogsState, useExtendedStateDispatcher: useLogsDispatcher } = createExtendedState<LogsState>()

const createLogStateDispatcherCreator = <Args extends unknown[]>(
    stateMapper: ((currentState: LogsState, ...args: Args) => Partial<LogsState>) | null,
    updateLogsPromise = true
): () => (...args: Args) => void => {
    /**
     * This dispatcher creator function has 3 nests in order to force whoever is calling them
     * to pass the dependencies (EG the mapper) in a way that every render wouldn't cause a further
     * re-render on its useCallback
     */

    return () => {
        const logsUsecase = useAppUsecase('containerLogsUsecase')
        const dispatch = useLogsDispatcher()

        return useCallback(
            (...args) => {
                dispatch((currentState) => {
                    /** First remap any of the parameters */
                    const stateUpdate = stateMapper ? stateMapper(currentState, ...args) : {}
                    const newState = { ...currentState, ...stateUpdate }

                    let logsPromise: Promise<readonly CybusLog[]> | null

                    if (updateLogsPromise) {
                        /** Fetch logs again */
                        const { source, resource, lines, startDate, endDate, levels } = newState
                        logsPromise = logsUsecase.fetchLogs(source, {
                            resource,
                            lines,
                            start: startDate ?? null,
                            end: endDate ?? null,
                            levels: isArrayNotEmpty(levels) ? levels : null,
                        })
                    } else {
                        /** Don't fetch logs and use previous ones */
                        logsPromise = newState.logsPromise
                    }

                    /** Finally, just dispatch the changes */
                    return { ...stateUpdate, logsPromise }
                })
            },
            [logsUsecase, stateMapper, updateLogsPromise]
        )
    }
}

export const useLogsLoader = createLogStateDispatcherCreator(null)

export const useLevelTogglerDispatcher = createLogStateDispatcherCreator(({ levels }, level: CybusLogLevel) => ({
    levels: Array.from(toggleReadonlySet(new Set(levels), level)),
}))

export const useDateRangeDispatcher = createLogStateDispatcherCreator(
    (_, { start, end }: Readonly<{ start: LogsState['startDate'], end: LogsState['endDate'] }>) => ({
        startDate: start,
        /** if start > end then the range is inconsistent, so treat it as open-ended instead of storing corrupted state */
        endDate: isDateAfter(start, end) ? null : end,
    })
)

export const useSelectedLogDispatcher = createLogStateDispatcherCreator((_, selectedLog: LogsState['selectedLog']) => ({ selectedLog }), false)

const createLogsStateHook = <Prop extends keyof LogsState>(prop: Prop): () => LogsState[Prop] =>
    nameFunction(() => useLogsState((s) => s[prop]), `useLogState${prop}`)

export const useSelectedLog = createLogsStateHook('selectedLog')
export const useResource = createLogsStateHook('resource')
export const useSource = createLogsStateHook('source')
export const useLevels = createLogsStateHook('levels')
export const useStartDate = createLogsStateHook('startDate')
export const useEndDate = createLogsStateHook('endDate')
const useLogsPromise = createLogsStateHook('logsPromise')

export const useLogs = (): readonly CybusLog[] | ConnectwareError | null => {
    const promise = useLogsPromise()
    const sync = usePromise<readonly CybusLog[] | null, ConnectwareError>(promise)
    return isPending(sync) ? null : sync.value
}

const initialState = { levels: [], logsPromise: null, selectedLog: null }

export type LogsReloadProps = Readonly<{
    /** A new value reloads the logs, resetting the filters, e.g. the resource they belong to, so they follow its changes */
    reloadOn?: unknown
}>

type ProviderProps = Optional<Omit<LogsState, keyof typeof initialState>, 'lines' | 'startDate' | 'endDate'> & LogsReloadProps

const Loader: FC = () => {
    const promise = useLogsPromise()
    const loadLogs = useLogsLoader()

    const hasNotLoaded = promise === null

    useEffect(() => {
        if (hasNotLoaded) {
            loadLogs()
        }
    }, [loadLogs, hasNotLoaded])
    return null
}

export const LogsProvider: FC<PropsWithChildren<ProviderProps>> = ({
    lines = 'limited',
    startDate = null,
    endDate = null,
    resource,
    source,
    reloadOn,
    children,
}) => {
    /** A new value resets the store, and so reloads the logs, so it only changes with the props, not with every render */
    const value = useMemo(
        () => ({ ...initialState, startDate, endDate, source, lines, resource }),
        [startDate, endDate, source, lines, resource, reloadOn]
    )

    return (
        <LogsStateProvider value={value}>
            <Loader />
            {children}
        </LogsStateProvider>
    )
}
